import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { AuthService, LogService, Notification, NotificationService, Ticket, TimeUtil } from '@ridango/core';
import { CompanyHistoryTicket, PurchaseHistory } from './purchase-history.model';

import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

import { Angulartics2 } from 'angulartics2';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';

import { PurchaseHistoryState } from './+state';
import { PurchaseHistoryActions } from './+state/purchase-history.actions';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';

@Injectable()
export class PurchaseHistoryService {
    constructor(
        @Inject('ENV') private env: Env,
        private ls: LogService,
        private http: HttpClient,
        private actions: PurchaseHistoryActions,
        private ngRedux: NgRedux<PurchaseHistoryState>,
        private notifications: NotificationService,
        private datePipe: DatePipe,
        private angulartics2: Angulartics2,
        private translate: TranslateService,
        private authService: AuthService
    ) {}

    fetchPurchaseHistory(
        start: Date,
        end: Date,
        companyId = null
    ): Observable<PurchaseHistory | CompanyHistoryTicket[]> {
        const s = this.datePipe.transform(start, 'yyyy-MM-dd');
        const e = this.datePipe.transform(end, 'yyyy-MM-dd');

        const params: any = {};
        if (this.env.HISTORY_CHANNEL) {
            params.channel = this.env.HISTORY_CHANNEL;
        }

        const url = companyId
            ? `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/company/${companyId}/from/${s}/to/${e}/history`
            : `${this.env.API_BASE_URL}/${this.env.REGION_ID}/tickets/history/${s}/${e}`;

        return this.http.get<PurchaseHistory | CompanyHistoryTicket[]>(url, {
            params: Object.keys(params).length > 0 ? params : undefined
        });
    }

    // TODO: Remove this from Klaipeda Corp and switch for fetchPurchaseHistory()
    fetchCompanyPurchaseHistoryCarts(start: Date, end: Date, companyId: any): Observable<any> {
        const s = this.datePipe.transform(start, 'yyyy-MM-dd');
        const e = this.datePipe.transform(end, 'yyyy-MM-dd');
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/company/${companyId}/from/${s}/to/${e}/history`;
        return this.http.get(url);
    }

    getPdf(lang: string, token: string = null) {
        const payload = {
            token: token,
            lang: lang
        };

        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/pdf/reciept/spa`;
        this.angulartics2.eventTrack.next({
            action: 'download',
            properties: { category: 'pdf', label: 'from PurchaseHistoryService' }
        });
        this.http
            .post(url, payload, {
                responseType: 'blob'
            })
            .subscribe(
                async (data: Blob) => {
                    this.ls.log(this, 'getPdf data', data);
                    if (Capacitor.isNativePlatform()) {
                        const encodedData = await this.blobToBase64(data, 'application/pdf');
                        const result = await Filesystem.writeFile({
                            path: 'receipt.pdf',
                            data: encodedData,
                            directory: Directory.Data
                        });

                        await FileOpener.open({
                            filePath: result.uri,
                            contentType: 'application/pdf'
                        });
                    } else {
                        saveAs(data, 'receipt.pdf');
                    }
                },
                (err) => {
                    this.ls.log(this, 'getPdf error', err);
                    this.notifications.add(
                        new Notification(this.notifications.notificationTypes.ERROR, 'LOCAL_GETTING_PDF_FAILED')
                    );
                    window.scroll(0, 0);
                },
                () => {
                    this.ls.log(this, 'getPdf ()', 'END');
                }
            );
    }

    downloadPurchaseHistoryPdf(start: Date, end: Date) {
        const token = this.authService.getLocalAccessToken();
        if (!token) {
            console.error('No authentication token available');
            return;
        }

        if (!start || !end) {
            console.error('Start date and end date are required');
            return;
        }

        this.requestHistoryPdf(token, start, end);
    }

    async getPdfPut(token: string = null, shoppingCartId: number = null, ticketId?: string) {
        const payload = {
            jwt_token: token,
            region_id: this.env.REGION_ID,
            lang: this.translate.currentLang
        };

        let url: string;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/purchase/${ticketId}/receipt/channel/web?lang=${this.translate.currentLang}`;
        } else {
            url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/pdf/reciept/spa`;
        }
        this.angulartics2.eventTrack.next({
            action: 'download',
            properties: { category: 'pdf', label: 'from PurchaseHistoryService' }
        });
        try {
            let data;
            if (this.env.IS_NEW_AFC) {
                const response = await fetch(url, { headers: { Accept: 'application/pdf' } });
                data = await response.blob();
                if (data.type !== 'application/pdf')
                    new Error('PDF response is not application/pdf, got: ' + data?.type);
            } else {
                data = await this.http.put(url, payload, { responseType: 'blob' }).toPromise();
            }
            this.ls.log(this, 'getPdf data', data);

            let fileName: string;
            if (shoppingCartId) {
                fileName = `A${shoppingCartId}.pdf`;
            } else {
                const translatedFileName = this.translate.instant('PDF_RECEIPT_FILE');
                fileName =
                    translatedFileName && !translatedFileName.startsWith('[MISSING]')
                        ? `${translatedFileName}.pdf`
                        : 'receipt.pdf';
            }
            if (Capacitor.isNativePlatform()) {
                const encodedData = await this.blobToBase64(data, 'application/pdf');
                const result = await Filesystem.writeFile({
                    path: fileName,
                    data: encodedData,
                    directory: Directory.Data
                });

                await FileOpener.open({
                    filePath: result.uri,
                    contentType: 'application/pdf'
                });
            } else {
                saveAs(data, fileName);
            }
        } catch (err) {
            this.ls.log(this, 'getPdf error', err);
            this.notifications.add(
                new Notification(this.notifications.notificationTypes.ERROR, 'LOCAL_GETTING_PDF_FAILED')
            );
            window.scroll(0, 0);
        }
    }

    setTickets(tickets: Ticket[]) {
        this.ngRedux.dispatch(this.actions.setTickets(tickets));
    }
    setCarts(carts: any[]) {
        this.ngRedux.dispatch(this.actions.setCarts(carts));
    }
    setIsLoading(isLoading: boolean) {
        this.ngRedux.dispatch(this.actions.setIsLoading(isLoading));
    }

    private async blobToBase64(blob: Blob, mimeType: string): Promise<string> {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrlPrefix = `data:${mimeType};base64,`;
                const base64WithDataUrlPrefix = reader.result.toString();
                const base64 = base64WithDataUrlPrefix.replace(dataUrlPrefix, '');
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    private requestHistoryPdf(token: string, start: Date, end: Date): void {
        const regionId = this.env.REGION_ID;
        const lang = this.translate.currentLang || 'et';
        const baseUrl = this.env.CORE_API_URL?.replace('/core', '') || '';
        const url = `${baseUrl}/v2/${regionId}/cart/pdf/purchase-history`;

        const requestBody: any = {
            jwt_token: token,
            region_id: regionId,
            lang: lang,
            startDate: TimeUtil.formatDateToISO(start),
            endDate: TimeUtil.formatDateToISO(end)
        };

        if (this.env.HISTORY_CHANNEL) {
            requestBody.channel = this.env.HISTORY_CHANNEL;
        }

        const headers = new HttpHeaders({
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
        });

        this.http.put(url, requestBody, { headers, responseType: 'blob' }).subscribe({
            next: (blob: Blob) => saveAs(blob, 'purchase-history.pdf'),
            error: (error) => console.error('Error downloading purchase history PDF:', error)
        });
    }
}
